import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { Button, ThemedText, ConfirmDialog, Pagination, ErrorState } from '../components';
import labReportService, { getLabReportStatusLabel } from '../api/services/labReportService';
import { extractPagedResponse } from '../utils/responseUtils';
import { usePagination } from '../hooks/usePagination';
import { PageContainer } from '../components/PageStyles';
import { StyledSelect as Select } from '../components/FormStyles';
import { LabReportStatusColors, withOpacity } from '../constants/statusColors';
import { Upload, Trash2, Eye, FlaskConical, FileText, FileSpreadsheet } from 'lucide-react';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};
`;

const FiltersRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  min-width: 250px;
  &:focus { outline: none; border-color: ${props => props.theme.colors.tint}; }
`;

const TableContainer = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: ${props => props.theme.colors.backgroundSecondary};
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  white-space: nowrap;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  cursor: pointer;
  &:hover { background-color: ${props => props.theme.colors.hover}; }
  &:not(:last-child) { border-bottom: 1px solid ${props => props.theme.colors.border}; }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  ${props => {
    const c = LabReportStatusColors[props.$status] || props.theme.colors.muted;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}
`;

const MatchBadge = styled.span`
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  ${props => {
    switch (props.$status) {
      case 'AutoLinked': return 'background-color: #dcfce7; color: #166534;';
      case 'ManuallyLinked': return 'background-color: #dbeafe; color: #1e40af;';
      case 'Suggested': return 'background-color: #fef3c7; color: #92400e;';
      default: return `background-color: ${props.theme.colors.backgroundSecondary}; color: ${props.theme.colors.muted};`;
    }
  }}
`;

const ConfidenceBadge = styled.span`
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => {
    const val = props.$value;
    if (val >= 0.8) return '#dcfce7';
    if (val >= 0.5) return '#fef3c7';
    return '#fee2e2';
  }};
  color: ${props => {
    const val = props.$value;
    if (val >= 0.8) return '#166534';
    if (val >= 0.5) return '#92400e';
    return '#991b1b';
  }};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xxl};
  color: ${props => props.theme.colors.muted};
  gap: ${props => props.theme.spacing.md};
`;

export const LabReportsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const pagination = usePagination(20);
  const [reports, setReports] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const fetchReports = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      if (!silent) setFetchError(null);
      const response = await labReportService.getLabReports({
        search, status: statusFilter, page: pagination.currentPage, pageSize: pagination.pageSize,
      });
      const { items, totalCount } = extractPagedResponse(response);
      setReports(items);
      setTotalCount(totalCount);
    } catch (err) {
      if (!silent) {
        setFetchError(err?.response?.data?.message || 'Failed to load lab reports. Please check your connection and try again.');
        toast.error('Failed to load lab reports');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [search, statusFilter, pagination.currentPage, pagination.pageSize]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // Auto-refresh when reports are in-flight (Queued or Processing)
  useEffect(() => {
    const hasProcessing = reports.some(r =>
      r.status === 'Queued' || r.status === 'Processing'
    );
    if (!hasProcessing) return;

    const interval = setInterval(() => fetchReports(true), 15000);
    return () => clearInterval(interval);
  }, [reports, fetchReports]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await labReportService.deleteLabReport(deleteId);
      toast.success('Lab report deleted');
      setDeleteId(null);
      fetchReports();
    } catch { toast.error('Failed to delete lab report'); }
  };

  return (
    <Layout>
      <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <ThemedText $size="h2" $weight="bold">Lab Reports</ThemedText>
          <Button onClick={() => navigate('/lab-reports/upload')}>
            <Upload size={16} style={{ marginRight: 6 }} /> Upload Report
          </Button>
        </PageHeader>

        <FiltersRow theme={theme}>
          <SearchInput theme={theme} placeholder="Search reports..." value={search}
            onChange={e => { setSearch(e.target.value); pagination.resetPage(); }} />
          <Select theme={theme} value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); pagination.resetPage(); }}>
            <option value="">All Statuses</option>
            <option value="Queued">Queued</option>
            <option value="Processing">Processing</option>
            <option value="NeedsReview">Needs Review</option>
            <option value="Ready">Ready</option>
            <option value="Failed">Failed</option>
          </Select>
        </FiltersRow>

        {loading ? (
          <EmptyState theme={theme}>
            <ThemedText>Loading lab reports...</ThemedText>
          </EmptyState>
        ) : fetchError ? (
          <ErrorState
            theme={theme}
            title="Failed to load lab reports"
            message={fetchError}
            onRetry={() => fetchReports()}
          />
        ) : reports.length === 0 ? (
          <EmptyState theme={theme}>
            <FlaskConical size={48} />
            <ThemedText $size="lg">No lab reports found</ThemedText>
            <ThemedText $color="muted">Upload a lab report (PDF or Excel) to get started</ThemedText>
          </EmptyState>
        ) : (
          <>
            <TableContainer theme={theme}>
              <Table>
                <Thead theme={theme}>
                  <tr>
                    <Th theme={theme}>Report #</Th>
                    <Th theme={theme}>Lab</Th>
                    <Th theme={theme}>Status</Th>
                    <Th theme={theme}>Date Reported</Th>
                    <Th theme={theme}>Customer</Th>
                    <Th theme={theme}>Samples</Th>
                    <Th theme={theme}>Match</Th>
                    <Th theme={theme}>Confidence</Th>
                    <Th theme={theme}>Created</Th>
                    <Th theme={theme}>Actions</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {reports.map(r => (
                    <Tr key={r.id} theme={theme} onClick={() => navigate(`/lab-reports/${r.id}`)}>
                      <Td theme={theme}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {r.sourceType === 'Excel'
                            ? <FileSpreadsheet size={14} color="#16a34a" />
                            : <FileText size={14} color={theme.colors.muted} />}
                          {r.reportNumber || r.originalFileName}
                        </span>
                      </Td>
                      <Td theme={theme}>{r.labName || '-'}</Td>
                      <Td theme={theme}>
                        <StatusBadge $status={r.status}>{getLabReportStatusLabel(r.status)}</StatusBadge>
                      </Td>
                      <Td theme={theme}>{r.dateReported ? new Date(r.dateReported).toLocaleDateString() : '-'}</Td>
                      <Td theme={theme}>{r.customerName || '-'}</Td>
                      <Td theme={theme}>{r.sampleCount || 0}</Td>
                      <Td theme={theme}>
                        {r.matchStatus && r.matchStatus !== 'Unmatched' ? (
                          <MatchBadge theme={theme} $status={r.matchStatus}>
                            {r.matchStatus === 'AutoLinked' ? 'Auto-linked' :
                             r.matchStatus === 'ManuallyLinked' ? 'Linked' :
                             r.matchStatus === 'Suggested' ? 'Suggested' : r.matchStatus}
                          </MatchBadge>
                        ) : '-'}
                      </Td>
                      <Td theme={theme}>
                        {r.overallConfidence != null ? (
                          <ConfidenceBadge $value={r.overallConfidence}>{(r.overallConfidence * 100).toFixed(0)}%</ConfidenceBadge>
                        ) : '-'}
                      </Td>
                      <Td theme={theme}>{new Date(r.createdAt).toLocaleDateString()}</Td>
                      <Td theme={theme} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Button $variant="ghost" $size="small" onClick={() => navigate(`/lab-reports/${r.id}`)}>
                            <Eye size={14} />
                          </Button>
                          <Button $variant="ghost" $size="small" onClick={() => setDeleteId(r.id)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <Pagination {...pagination} totalCount={totalCount} onPageChange={pagination.goToPage} />
          </>
        )}

        <ConfirmDialog
          isOpen={!!deleteId}
          title="Delete Lab Report"
          message="Are you sure you want to delete this lab report? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      </PageContainer>
    </Layout>
  );
};
